import { useState, useEffect, useCallback, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { acquisitionsApi, type Vendor } from "@/lib/api/acquisitions";
import { useToast } from "@/contexts/ToastContext";
import { Store, Plus, Loader2, Phone, Mail, UserX } from "lucide-react";

export default function VendorsPage() {
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"createdAt" | "name" | "code">("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await acquisitionsApi.vendors.list({
        search: search || undefined,
        isActive: activeFilter === "all" ? undefined : activeFilter === "active",
        limit: 50,
        sortBy,
        sortOrder,
      });
      setVendors(res.vendors);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to load vendors", err);
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const deactivateVendor = async (e: MouseEvent<HTMLButtonElement>, row: Vendor) => {
    e.stopPropagation();
    if (!row.isActive || deactivatingId) return;
    setDeactivatingId(row.id);
    try {
      await acquisitionsApi.vendors.update(row.id, { isActive: false });
      success(`${row.name} is now inactive.`);
      await fetchVendors();
    } catch (err) {
      console.error(err);
      toastError("Could not deactivate vendor.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const columns: DataTableColumn<Vendor>[] = [
    {
      id: "name",
      header: "Vendor Name",
      cell: (row) => (
        <button
          onClick={() => navigate(`/acquisitions/vendors/${row.id}`)}
          className="font-medium text-primary hover:underline text-left"
        >
          {row.name}
        </button>
      ),
    },
    {
      id: "code",
      header: "Code",
      cell: (row) => <span className="font-mono text-sm text-slate-600">{row.code}</span>,
      hideOnMobile: true,
    },
    {
      id: "contact",
      header: "Contact",
      cell: (row) => (
        <div className="flex flex-col gap-0.5 text-sm text-slate-600">
          {row.contactPerson && <span>{row.contactPerson}</span>}
          {row.email && (
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {row.email}
            </span>
          )}
          {row.phone && (
            <span className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              {row.phone}
            </span>
          )}
        </div>
      ),
      hideOnMobile: true,
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => (
        <StatusBadge variant={row.isActive ? "success" : "neutral"}>
          {row.isActive ? "Active" : "Inactive"}
        </StatusBadge>
      ),
      width: "w-24",
    },
    {
      id: "currency",
      header: "Currency",
      cell: (row) => <span className="text-sm text-slate-600">{row.currency}</span>,
      hideOnMobile: true,
      width: "w-20",
    },
    {
      id: "actions",
      header: "",
      cell: (row) =>
        row.isActive ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-slate-600"
            disabled={deactivatingId === row.id}
            onClick={(e) => deactivateVendor(e, row)}
          >
            {deactivatingId === row.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <UserX className="mr-1 h-4 w-4" />
                Deactivate
              </>
            )}
          </Button>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
      width: "w-36",
      align: "right",
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Vendors"
        description="Manage suppliers and booksellers for acquisitions"
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Vendors" }]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search vendors..."
        actions={
          <Button onClick={() => navigate("/acquisitions/vendors/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Add Vendor
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="createdAt">Date added</SelectItem>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="code">Code</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as typeof sortOrder)}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Order" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Descending</SelectItem>
            <SelectItem value="asc">Ascending</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">
          {total} vendor{total !== 1 ? "s" : ""}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={vendors}
        rowKey={(row) => row.id}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<Store className="h-6 w-6 text-slate-400" />}
            title="No vendors yet"
            description="Add your first vendor to start placing acquisition orders."
            action={
              <Button onClick={() => navigate("/acquisitions/vendors/new")}>
                <Plus className="mr-2 h-4 w-4" />
                Add Vendor
              </Button>
            }
          />
        }
      />
    </PageContainer>
  );
}
